import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight, Phone } from "lucide-react";
import { NAV, PORTALS, COMPANY } from "../../content/site";
import { NavDropdown } from "./NavDropdown";

const portalItems = PORTALS.map((p) => ({ label: p.label, href: p.href, desc: "Opens necos.app" }));

const Logo = () => (
  <Link to="/" data-testid="header-logo-link" className="flex shrink-0 items-center" aria-label="NECOS.FM home">
    <img src="/brand/logo-light.webp" alt="NECOS.FM" className="h-8 w-auto sm:h-9" width="130" height="36" />
  </Link>
);

const MobileGroup = ({ item }) => {
  const [open, setOpen] = useState(false);
  if (!item.items)
    return (
      <Link to={item.to} data-testid={`mobile-nav-${item.id}`} className="block py-4 text-2xl font-display font-medium">
        {item.label}
      </Link>
    );
  return (
    <div>
      <button
        type="button"
        data-testid={`mobile-nav-${item.id}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between py-4 text-left text-2xl font-display font-medium"
      >
        {item.label}
        <ChevronDown className={`h-5 w-5 text-gold transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <ul className="mb-3 space-y-1 border-l border-gold/20 pl-4">
          {item.items.map((it) => (
            <li key={it.to}>
              <Link to={it.to} className="block py-2 text-base text-mist hover:text-gold" data-testid={`mobile-nav-${item.id}-${it.to === "/facilities-management" ? "overview" : it.to.split("/").pop()}`}>
                {it.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MobileMenu = ({ onClose }) => createPortal(
  <div
    id="mobile-menu"
    data-testid="mobile-menu"
    className="menu-in fixed inset-x-0 bottom-0 top-[108px] z-[45] overflow-y-auto border-t hairline bg-navy/[0.98] backdrop-blur-xl min-[1180px]:hidden"
  >
    <nav aria-label="Mobile" className="container-x divide-y divide-gold/10 pb-10 pt-4">
      {NAV.map((item) => (
        <MobileGroup key={item.id} item={item} />
      ))}
      <div className="py-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">Portals</p>
        <ul className="mt-3 space-y-2">
          {PORTALS.map((p) => (
            <li key={p.id}>
              <a href={p.href} target="_blank" rel="noopener noreferrer" onClick={onClose} data-testid={`mobile-portal-${p.id}`} className="flex items-center gap-2 text-base text-paper hover:text-gold">
                {p.label} <ArrowUpRight className="h-4 w-4 opacity-60" aria-hidden="true" />
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="pt-6">
        <a href={COMPANY.hotlineTel} className="flex items-center gap-2 font-mono text-lg text-gold" data-testid="mobile-menu-hotline">
          <Phone className="h-4 w-4" aria-hidden="true" /> {COMPANY.hotline}
        </a>
      </div>
    </nav>
  </div>,
  document.body
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header data-testid="site-header" className="border-b hairline bg-navy/85 backdrop-blur-xl">
      <div className="container-x flex h-16 items-center justify-between gap-6">
        <Logo />
        <nav aria-label="Main" className="hidden items-center gap-0.5 min-[1180px]:flex">
          {NAV.map((item) =>
            item.items ? (
              <NavDropdown key={item.id} id={item.id} label={item.label} items={item.items} mega={item.mega} />
            ) : (
              <NavLink
                key={item.id}
                to={item.to}
                data-testid={`nav-${item.id}`}
                className={({ isActive }) =>
                  `rounded-full px-3.5 py-2 text-[14.5px] font-medium transition-colors ${isActive ? "text-gold" : "text-paper/90 hover:text-gold"}`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="flex items-center gap-2">
          <div className="hidden min-[1180px]:block">
            <NavDropdown id="portals" label="Portals" items={portalItems} align="right" compact />
          </div>
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/25 text-paper min-[1180px]:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && <MobileMenu onClose={() => setOpen(false)} />}
    </header>
  );
};
